import type { ColumnDef } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import { MetricListItem, MetricListItemGrid } from "@/components/ListItem";
import Table, { TableHeading } from "@/components/Table";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { scrollToElement } from "@/lib/scrollHelpers";
import { membershipApplicationRequirementHref } from "@/features/person/utils/membershipApplicationLinks";
import {
  requirementRowHasDestination,
  requirementRowScrollsInPage,
  type RequirementRow
} from "@/features/person/utils/pgm-requirements";

const REQUIREMENT_NAME_CLASS = "min-w-0 max-w-full whitespace-normal break-words";
const REQUIREMENT_LINK_CLASS = `${REQUIREMENT_NAME_CLASS} underline underline-offset-2`;

export const DETAIL_TABLE_HEADING_PROPS = {
  headingSize: "small",
  topSpacing: "compact"
} as const;

function RequirementName({
  label,
  hasDestination,
  onInPageNavigate
}: {
  label: string;
  hasDestination: boolean;
  onInPageNavigate?: () => void;
}) {
  if (onInPageNavigate) {
    return (
      <button
        type="button"
        className={`bg-transparent p-0 border-0 text-inherit ${REQUIREMENT_LINK_CLASS} cursor-pointer text-left`}
        onClick={onInPageNavigate}
      >
        {label}
      </button>
    );
  }

  return <span className={hasDestination ? REQUIREMENT_LINK_CLASS : REQUIREMENT_NAME_CLASS}>{label}</span>;
}

export function toMetricListItemProps(metric: MetricCardType<Path>) {
  return {
    iconName: metric.iconName ?? "info",
    overline: metric.label,
    headline: metric.value ?? "--",
    tooltipTitle: typeof metric.tooltip?.title === "string" ? metric.tooltip.title : undefined,
    tooltipSupportingText:
      typeof metric.tooltip?.supportingText === "string" ? metric.tooltip.supportingText : undefined
  };
}

export const requirementsColumns: ColumnDef<RequirementRow, unknown>[] = [
  {
    accessorKey: "name",
    header: "Requirement",
    cell: ({ row }) => (
      <RequirementName
        label={row.original.name}
        hasDestination={requirementRowHasDestination(row.original)}
        onInPageNavigate={
          requirementRowScrollsInPage(row.original)
            ? () => {
                const sectionId = row.original.scrollToSectionId;
                if (sectionId) scrollToElement(sectionId);
              }
            : undefined
        }
      />
    ),
    enableSorting: false,
    size: 300,
    meta: { headerClassName: "hide-header-in-column", className: "whitespace-normal" }
  },
  {
    accessorKey: "completionDate",
    header: "Completion Date",
    cell: ({ row }) => <span>{row.original.completionDate || "--"}</span>,
    enableSorting: false,
    size: 150
  },
  buildTagColumn<RequirementRow>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => row.status,
    getTagType: (row) => row.statusType,
    getIconName: (row) => (row.status === "Locked" ? "lock" : undefined),
    getTooltipText: (row) => (row.status === "Locked" ? "Previous Requirement(s) Must Be Met First" : undefined),
    getTooltip: (row) => row.statusTooltip,
    size: 120
  })
];

export interface PgmRequirementsSectionProps {
  sectionId: string;
  heading: string;
  body?: string;
  dateMetrics: MetricCardType<Path>[];
  dateMetricsTestId: string;
  requirements: RequirementRow[];
  tableTestId: string;
  emptyTitle: string;
  emptyCaption: string;
  loading?: boolean;
  /** Defaults to nested detail size (`small`). Use `default` when this section is first under a tab bar. */
  headingSize?: "default" | "small";
  /** Defaults to nested `compact`. Use `default` when this section is first under a tab bar. */
  topSpacing?: "default" | "compact" | "none";
}

export default function PgmRequirementsSection({
  sectionId,
  heading,
  body,
  dateMetrics,
  dateMetricsTestId,
  requirements,
  tableTestId,
  emptyTitle,
  emptyCaption,
  loading = false,
  headingSize = DETAIL_TABLE_HEADING_PROPS.headingSize,
  topSpacing = DETAIL_TABLE_HEADING_PROPS.topSpacing
}: PgmRequirementsSectionProps) {
  return (
    <>
      <div id={sectionId}>
        <TableHeading heading={heading} body={body} headingSize={headingSize} topSpacing={topSpacing} />
      </div>
      {!loading && dateMetrics.length > 0 ? (
        <MetricListItemGrid className="-mt-2" testId={dateMetricsTestId}>
          {dateMetrics.map((metric) => (
            <MetricListItem key={metric.label} {...toMetricListItemProps(metric)} />
          ))}
        </MetricListItemGrid>
      ) : null}
      <div className="mt-6">
        <Table
          data={requirements}
          columns={requirementsColumns}
          loading={loading}
          data-testid={tableTestId}
          getRowLink={(row) => {
            const href = membershipApplicationRequirementHref(row.original);
            if (href) return { to: href as Path };
            if (row.original.detailPath) {
              return { to: row.original.detailPath };
            }
            return null;
          }}
          getRowClasses={(row) => (requirementRowHasDestination(row.original) ? "" : "cursor-default")}
          renderNoDataEmptyState={() => <EmptyState title={emptyTitle} caption={emptyCaption} iconName="school" />}
        />
      </div>
    </>
  );
}
